import React, { useEffect, useState } from "react";
import { useAuth } from "../../auth/FirebaseAuth.js";
import { useParams } from "react-router-dom";
import { getUserById } from "../../models/UserFunctions.js";
import { getOrgById, getOrgByRef } from "../../models/OrgFunctions.js";
import Column from "../../layout/Column.js";
import Row from "../../layout/Row.js";
import Divider from "../../layout/Divider.js";
import Icon1 from "../../icons/Icon.js";
import Space from "../../layout/Space.js";
import InfoCell from "../../listviews/InfoCell.js";
import MLTF1 from "../../textfields/MLTF1.js";
import SecondaryButton from "../../buttons/SecondaryButton.js";

import PrimaryButton from "../../buttons/PrimaryButton.js";
import loginAs from "../../api/loginAs.js";
import {
  getDocDataById,
  queryCollection,
  updateDocDataField,
} from "../../models/DataFunctions.js";
import { useSnackbar } from "notistack";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import { useAppContext } from "../../config/AppContext.js";
import { deleteField } from "firebase/firestore";
import WarningButton from "../../buttons/WarningButton.js";
import updateDocDataFieldApi from "../../api/database/updateDocDataFieldApi.js";
import deleteMember from "../../api/delete/deleteMember.js";
import { getOrRefreshFirebaseToken } from "../../config/getFirebaseToken.js";
import deleteOrg from "../../api/delete/deleteOrg.js";
import TextField2 from "../../textfields/TextField2.js";
import { EasySwitch } from "../../textfields/EasySwitch.js";
import { getUserClaims, addUserClaim, updateUserClaim, deleteUserClaim } from "../../api/auth/customClaims.js";

function User({ navigateWorkspace, handleLoginAs }) {
  const { id } = useParams(); // Access the `id` parameter from the URL
  const { currentUser, loading } = useAuth();

  const [userData, setUserData] = useState(null);
  const [orgData, setOrgData] = useState(null);
  const [actualUserData, setActualUserData] = useState(null);
  const [customClaims, setCustomClaims] = useState(null);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [newClaimKey, setNewClaimKey] = useState("");
  const [newClaimValue, setNewClaimValue] = useState("");
  const [newClaimType, setNewClaimType] = useState("string");
  const [editingClaim, setEditingClaim] = useState(null);
  const [editClaimValue, setEditClaimValue] = useState("");

  const { enqueueSnackbar } = useSnackbar();
  const handleClickVariant = (message, variant) => {
    enqueueSnackbar(message, { variant });
  };

  const { appState, setAppState, resetAppStateLogin } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;
  const [activated_login_as, setActivatedLoginAs] = useState(false);
  const [confirm, setConfirm] = useState("");

  const handleDeleteUser = async () => {
    const token = await getOrRefreshFirebaseToken();
    await deleteMember(orgData.id, actualUserData.id, token, prefix);
    handleClickVariant("Utilisateur supprimé", "success");
    navigateWorkspace("/admin/users");
  };

  const handleDeleteOrg = async () => {
    const token = await getOrRefreshFirebaseToken();
    if (!actualUserData.org_id) {
      handleClickVariant("Utilisateur sans organisation", "error");
      return;
    }
    let actualOrg = await getOrgById(actualUserData.org_id);
    if (!actualOrg) {
      handleClickVariant("Organisation non trouvée", "error");
      return;
    }
    await deleteOrg(actualOrg.id, token, prefix);
    handleClickVariant("Org supprimée", "success");
    navigateWorkspace("/admin/users");
  };

  const handleBan = async () => {
    if (actualUserData.forum_banned) {
      await updateDocDataFieldApi(
        actualUserData.ref,
        "forum_banned",
        false,
        prefix
      );
      handleClickVariant("Utilisateur débanni", "success");
    } else {
      await updateDocDataFieldApi(
        actualUserData.ref,
        "forum_banned",
        true,
        prefix
      );
      handleClickVariant("Utilisateur banni", "success");
    }
    handleFetchData();
  };

  // Fetch user, org, clients, and article data
  const handleFetchData = async () => {
    if (currentUser) {
      try {
        // Fetch user data
        const user = await getUserById(currentUser.uid);
        if (user.org_id && user.org_id !== undefined) {
          // Fetch organization data using user's org reference
          const org = await getOrgById(user.org_id);

          const actual_user = await getUserById(id);
          // Update state with fetched data
          setUserData(user);
          setOrgData(org);
          setActualUserData(actual_user);
          
          // Fetch custom claims
          await fetchCustomClaims(id);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  };

  const handleSetFake = async () => {
    await updateDocDataFieldApi(
      actualUserData.ref,
      "beta_type",
      "fake",
      prefix
    );
    handleClickVariant("Utilisateur mis à jour", "success");
    handleFetchData();
  };
  const handleSetDenys = async () => {
    await updateDocDataFieldApi(
      actualUserData.ref,
      "beta_type",
      "denys_related",
      prefix
    );
    handleClickVariant("Utilisateur mis à jour", "success");
    handleFetchData();
  };
  const handleReset = async () => {
    await updateDocDataFieldApi(actualUserData.ref, "beta_type", "", prefix);
    handleClickVariant("Utilisateur mis à jour", "success");
    handleFetchData();
  };

  const fetchCustomClaims = async (userId) => {
    setClaimsLoading(true);
    try {
      const token = await getOrRefreshFirebaseToken();
      const claimsData = await getUserClaims(userId, prefix, token);
      console.log("got claims data", claimsData);
      setCustomClaims(claimsData.claims || []);
    } catch (error) {
      console.error("Error fetching custom claims:", error);
      handleClickVariant("Erreur lors du chargement des claims", "error");
    } finally {
      setClaimsLoading(false);
    }
  };

  const handleAddClaim = async () => {
    if (!newClaimKey.trim() || !newClaimValue.trim()) {
      handleClickVariant("Veuillez remplir la clé et la valeur", "error");
      return;
    }

    if (customClaims && customClaims.hasOwnProperty(newClaimKey)) {
      handleClickVariant("Cette clé existe déjà", "error");
      return;
    }

    let processedValue = newClaimValue;
    if (newClaimType === "boolean") {
      processedValue = newClaimValue.toLowerCase() === "true";
    }

    try {
      const token = await getOrRefreshFirebaseToken();
      await addUserClaim(id, newClaimKey, processedValue, prefix, token);
      handleClickVariant("Claim ajouté avec succès", "success");
      setNewClaimKey("");
      setNewClaimValue("");
      setNewClaimType("string");
      await fetchCustomClaims(id);
    } catch (error) {
      console.error("Error adding claim:", error);
      handleClickVariant("Erreur lors de l'ajout du claim", "error");
    }
  };

  const handleEditClaim = (claimKey, currentValue) => {
    setEditingClaim(claimKey);
    setEditClaimValue(String(currentValue));
  };

  const handleSaveEdit = async () => {
    if (!editClaimValue.trim()) {
      handleClickVariant("La valeur ne peut pas être vide", "error");
      return;
    }

    try {
      const token = await getOrRefreshFirebaseToken();
      let processedValue = editClaimValue;
      
      // Try to preserve the original type
      const originalValue = customClaims[editingClaim];
      if (typeof originalValue === 'boolean') {
        processedValue = editClaimValue.toLowerCase() === 'true';
      }
      
      await updateUserClaim(id, editingClaim, processedValue, prefix, token);
      handleClickVariant("Claim modifié avec succès", "success");
      setEditingClaim(null);
      setEditClaimValue("");
      await fetchCustomClaims(id);
    } catch (error) {
      console.error("Error updating claim:", error);
      handleClickVariant("Erreur lors de la modification du claim", "error");
    }
  };

  const handleCancelEdit = () => {
    setEditingClaim(null);
    setEditClaimValue("");
  };

  const handleDeleteClaim = async (claimKey) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le claim "${claimKey}" ?`)) {
      return;
    }

    try {
      const token = await getOrRefreshFirebaseToken();
      await deleteUserClaim(id, claimKey, prefix, token);
      handleClickVariant("Claim supprimé avec succès", "success");
      await fetchCustomClaims(id);
    } catch (error) {
      console.error("Error deleting claim:", error);
      handleClickVariant("Erreur lors de la suppression du claim", "error");
    }
  };

  useEffect(() => {
    if (currentUser) {
      handleFetchData(); // Fetch user data when currentUser is available
    }
  }, [currentUser]); // Dependency array ensures this runs when currentUser changes

  // Handle navigation to clients page
  const gotoUsers = () => {
    navigateWorkspace("/admin/users");
  };

  if (loading || !currentUser || !userData) {
    return <div></div>; // Show loading state while checking auth or if currentUser is null
  }

  return (
    <div className="flex flex-row w-full h-full overflow-auto ">
      {/* Left Column: Takes all available space */}
      <div className="flex flex-col h-full flex-grow pl-4">
        <Space y={20} />
        <div className="flex flex-row w-full">
          <Row
            align="left"
            className="w-full cursor-pointer"
            onClick={gotoUsers}
          >
            <div className="text-h2 font-semibold text-pr bg-transparent">
              Utilisateurs
            </div>
            <Icon1 icon_name="right" height="15px"></Icon1>
          </Row>

          <div className="flex-grow flex-row-reverse flex w-full">
            <Space x={10} />
            <PrimaryButton
              className="w-fit"
              disabled={actualUserData?.email === "william.bresso@gmail.com"}
              onClick={() => handleLoginAs(id)}
            >
              Login
            </PrimaryButton>
          </div>
        </div>
        <Space y={5} />
        <div className="text-hx font-semibold">
          {actualUserData?.display_name}
        </div>
        <Space y={5} />
        <div className="text-h3 font-light">
          {actualUserData?.tracking?.last_connection && (
            <>
              Dernière connexion le{" "}
              {new Date(
                actualUserData?.tracking?.last_connection?.seconds * 1000
              ).toLocaleString()}
            </>
          )}
        </div>
        <div className="flex flex-col gap-2">
          {actualUserData.beta_type === "fake" && (
            <div className=" rounded-xl py-0.5 text-shadow_50 bg-neutral-200 px-2 text-h2 font-semibold w-fit">
              FAKE
            </div>
          )}
          {actualUserData.beta_type === "denys_related" && (
            <div className=" rounded-xl py-0.5 text-shadow_50 bg-neutral-200 px-2 text-h2 font-semibold w-fit">
              DENYS RELATED
            </div>
          )}
          <SecondaryButton className="w-fit  " onClick={handleSetFake}>
            Set user to FAKE
          </SecondaryButton>
          <SecondaryButton className="w-fit" onClick={handleSetDenys}>
            Set user to DENYS-RELATED
          </SecondaryButton>
          <SecondaryButton className="w-fit" onClick={handleReset}>
            Reset
          </SecondaryButton>{" "}
          <WarningButton
            disabled={confirm !== "CONFIRMER"}
            className="w-fit"
            onClick={handleBan}
          >
            Ban from FORUM (or unban)
          </WarningButton>
          <WarningButton
            disabled={confirm !== "CONFIRMER"}
            className="w-fit"
            onClick={handleDeleteUser}
          >
            Delete User and Firebase Auth
          </WarningButton>
          <WarningButton
            disabled={confirm !== "CONFIRMER"}
            className="w-fit"
            onClick={handleDeleteOrg}
          >
            Delete associated Org and All Users and All Firebase Auth and All
            Folders
          </WarningButton>
          <TextField2
            className="max-w-c200"
            label={"CONFIRMER"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>
        <div className="flex flex-row w-full items-center mt-4 gap-2">
          <EasySwitch
            checked={actualUserData?.maintenance_proof}
            onChange={async (checked) => {
              await updateDocDataFieldApi(
                actualUserData.ref,
                "maintenance_proof",
                checked,
                prefix
              );
              handleClickVariant("Maintenance proof updated", "success");
              handleFetchData();
            }}
          ></EasySwitch>
          <div className="text-h3 font-light">Maintenance Proof</div>
        </div>
        <Space y={30} />
        {/* Displaying members and affiliates */}
        {/* Client contact information */}
      </div>

      <Divider orientation="vertical" />

      {/* Right Column: Fixed width */}
      <Column align="left" className="h-full max-w-c400 pl-2">
        <Space y={20} />
        <div className="text-h1 font-semibold">Informations</div>
        <Divider orientation="horizontal" />
        <Space y={5} />
        <Column align="left" className="w-full pr-2">
          <InfoCell title="ID" data={actualUserData?.id} />
          <InfoCell title="OID" data={actualUserData?.org_id} />

          <InfoCell
            title="Connected"
            data={
              actualUserData?.tracking?.last_connection
                ? new Date(
                    actualUserData?.tracking?.last_connection * 1000
                  ).toLocaleString()
                : "Never"
            }
          />
          <InfoCell title="Display Name" data={actualUserData?.display_name} />
          <InfoCell title="Email" data={actualUserData?.email} />
          <InfoCell title="Phone" data={actualUserData?.phone} />
        </Column>

        {/* Custom Claims section */}
        <Space y={25} />
        <div className="text-h1 font-semibold">Custom Claims</div>
        <Divider orientation="horizontal" />
        <Space y={5} />
        <Column align="left" className="w-full pr-2">
          {claimsLoading ? (
            <div className="text-h3 font-light">Chargement...</div>
          ) : customClaims ? (
            Object.keys(customClaims).length > 0 ? (
              Object.entries(customClaims).map(([key, value]) => (
                <div key={key} className="border border-gray-200 rounded-md p-3 mb-2">
                  <div className="flex justify-between items-start">
                    <div className="flex-grow">
                      <div className="text-h3 font-semibold text-gray-700">{key}</div>
                      {editingClaim === key ? (
                        <div className="mt-2 flex gap-2 items-center">
                          <TextField2
                            value={editClaimValue}
                            onChange={(e) => setEditClaimValue(e.target.value)}
                            placeholder="Nouvelle valeur"
                            className="flex-grow"
                          />
                          <SecondaryButton onClick={handleSaveEdit} className="w-fit">
                            Sauver
                          </SecondaryButton>
                          <SecondaryButton onClick={handleCancelEdit} className="w-fit">
                            Annuler
                          </SecondaryButton>
                        </div>
                      ) : (
                        <div 
                          className="mt-1 text-h3 font-light cursor-pointer hover:bg-gray-50 p-1 rounded"
                          onClick={() => handleEditClaim(key, value)}
                          title="Cliquer pour modifier"
                        >
                          {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </div>
                      )}
                    </div>
                    {editingClaim !== key && (
                      <div className="flex items-center ml-2">
                        <div 
                          className="cursor-pointer hover:bg-red-50 p-1 rounded"
                          onClick={() => handleDeleteClaim(key)}
                          title="Supprimer"
                        >
                          <Icon1 icon_name="delete" size={16} fill="#ef4444" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-h3 font-light">Aucun custom claim</div>
            )
          ) : (
            <div className="text-h3 font-light">Erreur de chargement</div>
          )}
        </Column>

        <Space y={15} />
        <div className="text-h2 font-semibold">Ajouter un claim</div>
        <Space y={5} />
        <Column align="left" className="w-full pr-2 gap-2">
          <TextField2
            label="Clé"
            value={newClaimKey}
            onChange={(e) => setNewClaimKey(e.target.value)}
            placeholder="Ex: role, permission, etc."
          />
          <div className="flex flex-col gap-1">
            <label className="text-h3 font-medium">Type de valeur</label>
            <select 
              value={newClaimType} 
              onChange={(e) => setNewClaimType(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-h3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="string">String</option>
              <option value="boolean">Boolean</option>
            </select>
          </div>
          <TextField2
            label="Valeur"
            value={newClaimValue}
            onChange={(e) => setNewClaimValue(e.target.value)}
            placeholder={newClaimType === "boolean" ? "true ou false" : "Ex: admin, moderator, etc."}
          />
          <PrimaryButton
            className="w-fit"
            onClick={handleAddClaim}
            disabled={!newClaimKey.trim() || !newClaimValue.trim()}
          >
            Ajouter Claim
          </PrimaryButton>
        </Column>

        {/* Notes section */}
        <Space y={25} />
        <div className="text-h1 font-semibold">Notes</div>
        <Divider orientation="horizontal" />
        <Space y={5} />
        <div className="pr-3 w-full">
          <MLTF1></MLTF1>
        </div>
        <div className="w-full flex flex-row-reverse px-3 py-1">
          <SecondaryButton>Enregistrer</SecondaryButton>
        </div>
      </Column>
    </div>
  );
}

export default User;
